import {
  boardContentsSchema,
  offlineSyncRequestSchema,
  offlineSyncResponseSchema,
  type BoardContents,
  type OfflineSyncOperation,
  type OfflineSyncResponse,
} from '@molt/shared';
import { z } from 'zod';
import { post } from './api';

const OFFLINE_BOARD_STORAGE_PREFIX = 'molt:offline-board:';
const OFFLINE_MOVE_STORAGE_PREFIX = 'molt:offline-card-moves:';
const OFFLINE_DESCRIPTION_STORAGE_PREFIX = 'molt:offline-card-descriptions:';
export const OFFLINE_DESCRIPTION_DRAFTS_CHANGED_EVENT =
  'molt:offline-card-descriptions-changed';

const cachedOfflineBoardSchema = z.strictObject({
  userId: z.string().min(1),
  shortLink: z.string().min(1),
  path: z.string().min(1),
  contents: boardContentsSchema,
  cachedAt: z.string().min(1),
});

const offlineMoveDraftSchema = z.strictObject({
  clientOperationId: z.string().min(1),
  cardId: z.string().min(1),
  fromListId: z.string().min(1),
  toListId: z.string().min(1),
  previousCardId: z.string().min(1).nullable(),
  nextCardId: z.string().min(1).nullable(),
  queuedAt: z.string().min(1),
});

const offlineMoveDraftsSchema = z.array(offlineMoveDraftSchema);

const offlineDescriptionDraftSchema = z.strictObject({
  clientOperationId: z.string().min(1),
  cardId: z.string().min(1),
  boardId: z.string().min(1),
  cardTitle: z.string().min(1),
  description: z.string(),
  fieldVersion: z.string().min(1).optional(),
  queuedAt: z.string().min(1),
  retryAfter: z.string().min(1).optional(),
});

const offlineDescriptionDraftsSchema = z.array(offlineDescriptionDraftSchema);

export async function syncOfflineOperations(
  operations: readonly OfflineSyncOperation[],
): Promise<OfflineSyncResponse> {
  return await post(
    '/me/offline-sync',
    offlineSyncResponseSchema,
    offlineSyncRequestSchema.parse({ operations }),
  );
}

export interface CachedOfflineBoard {
  readonly userId: string;
  readonly shortLink: string;
  readonly path: string;
  readonly contents: BoardContents;
  readonly cachedAt: string;
}

export function saveOfflineBoardSnapshot(input: {
  readonly userId: string;
  readonly shortLink: string;
  readonly path: string;
  readonly contents: BoardContents;
  readonly now?: Date;
}): void {
  const snapshot = cachedOfflineBoardSchema.safeParse({
    userId: input.userId,
    shortLink: input.shortLink,
    path: input.path,
    contents: input.contents,
    cachedAt: (input.now ?? new Date()).toISOString(),
  });
  if (!snapshot.success) return;

  const storage = offlineBoardStorage();
  if (storage === null) return;

  try {
    storage.setItem(
      offlineBoardStorageKey(input.userId, input.shortLink),
      JSON.stringify(snapshot.data),
    );
  } catch {
    /* Browser storage can be disabled or full; the online board still renders. */
  }
}

export function loadOfflineBoardSnapshot(
  userId: string | undefined,
  shortLink: string,
): CachedOfflineBoard | null {
  if (userId === undefined) return null;
  const storage = offlineBoardStorage();
  if (storage === null) return null;

  const key = offlineBoardStorageKey(userId, shortLink);
  let raw: string | null;
  try {
    raw = storage.getItem(key);
  } catch {
    return null;
  }
  if (raw === null) return null;

  let json: unknown;
  try {
    json = JSON.parse(raw);
  } catch {
    removeCorruptOfflineBoardSnapshot(storage, key);
    return null;
  }

  const parsed = cachedOfflineBoardSchema.safeParse(json);
  if (
    !parsed.success ||
    parsed.data.userId !== userId ||
    parsed.data.shortLink !== shortLink
  ) {
    removeCorruptOfflineBoardSnapshot(storage, key);
    return null;
  }

  return parsed.data;
}

export interface OfflineMoveDraft {
  readonly clientOperationId: string;
  readonly cardId: string;
  readonly fromListId: string;
  readonly toListId: string;
  readonly previousCardId: string | null;
  readonly nextCardId: string | null;
  readonly queuedAt: string;
}

export function loadOfflineMoveDrafts(
  userId: string | undefined,
  shortLink: string,
): readonly OfflineMoveDraft[] {
  if (userId === undefined) return [];
  const storage = offlineBoardStorage();
  if (storage === null) return [];

  const key = offlineMoveStorageKey(userId, shortLink);
  let raw: string | null;
  try {
    raw = storage.getItem(key);
  } catch {
    return [];
  }
  if (raw === null) return [];

  let json: unknown;
  try {
    json = JSON.parse(raw);
  } catch {
    removeCorruptOfflineBoardSnapshot(storage, key);
    return [];
  }

  const parsed = offlineMoveDraftsSchema.safeParse(json);
  if (!parsed.success) {
    removeCorruptOfflineBoardSnapshot(storage, key);
    return [];
  }
  return parsed.data;
}

export function saveOfflineMoveDrafts(
  userId: string | undefined,
  shortLink: string,
  moves: readonly OfflineMoveDraft[],
): void {
  if (userId === undefined) return;
  const storage = offlineBoardStorage();
  if (storage === null) return;

  const key = offlineMoveStorageKey(userId, shortLink);
  try {
    if (moves.length === 0) {
      storage.removeItem(key);
      return;
    }
    storage.setItem(key, JSON.stringify(offlineMoveDraftsSchema.parse(moves)));
  } catch {
    /* Browser storage can be disabled or full; the in-memory queue still renders. */
  }
}

export interface OfflineDescriptionDraft {
  readonly clientOperationId: string;
  readonly cardId: string;
  readonly boardId: string;
  readonly cardTitle: string;
  readonly description: string;
  readonly fieldVersion?: string | undefined;
  readonly queuedAt: string;
  readonly retryAfter?: string | undefined;
}

export function loadOfflineDescriptionDrafts(
  userId: string | undefined,
  shortLink: string | undefined,
): readonly OfflineDescriptionDraft[] {
  if (userId === undefined || shortLink === undefined) return [];
  const storage = offlineBoardStorage();
  if (storage === null) return [];

  const key = offlineDescriptionStorageKey(userId, shortLink);
  let raw: string | null;
  try {
    raw = storage.getItem(key);
  } catch {
    return [];
  }
  if (raw === null) return [];

  let json: unknown;
  try {
    json = JSON.parse(raw);
  } catch {
    removeCorruptOfflineBoardSnapshot(storage, key);
    return [];
  }

  const parsed = offlineDescriptionDraftsSchema.safeParse(json);
  if (!parsed.success) {
    removeCorruptOfflineBoardSnapshot(storage, key);
    return [];
  }
  return parsed.data;
}

export function saveOfflineDescriptionDrafts(
  userId: string | undefined,
  shortLink: string | undefined,
  drafts: readonly OfflineDescriptionDraft[],
  options: { readonly notify?: boolean | undefined } = {},
): void {
  if (userId === undefined || shortLink === undefined) return;
  const storage = offlineBoardStorage();
  if (storage === null) return;

  const key = offlineDescriptionStorageKey(userId, shortLink);
  try {
    if (drafts.length === 0) {
      storage.removeItem(key);
      if (options.notify !== false) dispatchOfflineDescriptionDraftsChanged();
      return;
    }
    storage.setItem(key, JSON.stringify(offlineDescriptionDraftsSchema.parse(drafts)));
    if (options.notify !== false) dispatchOfflineDescriptionDraftsChanged();
  } catch {
    /* Browser storage can be disabled or full; the in-memory queue still renders. */
  }
}

export function offlineMoveToOperation(move: OfflineMoveDraft): OfflineSyncOperation {
  return {
    clientOperationId: move.clientOperationId,
    kind: 'card.move',
    card: { source: 'server', id: move.cardId },
    list: { source: 'server', id: move.toListId },
    body: {
      previousCardId: move.previousCardId,
      nextCardId: move.nextCardId,
    },
  };
}

export function offlineDescriptionToOperation(
  draft: OfflineDescriptionDraft,
): OfflineSyncOperation {
  return {
    clientOperationId: draft.clientOperationId,
    kind: 'card.edit_description',
    card: { source: 'server', id: draft.cardId },
    description: draft.description,
    expectedVersion: draft.fieldVersion ?? null,
  };
}

export function pendingOfflineCardIds(
  moves: readonly OfflineMoveDraft[],
): ReadonlySet<string> {
  return new Set(moves.map((move) => move.cardId));
}

export function pendingOfflineDescriptionCardIds(
  drafts: readonly OfflineDescriptionDraft[],
): ReadonlySet<string> {
  return new Set(drafts.map((draft) => draft.cardId));
}

function offlineBoardStorageKey(userId: string, shortLink: string): string {
  return `${OFFLINE_BOARD_STORAGE_PREFIX}${userId}:${shortLink}`;
}

function offlineMoveStorageKey(userId: string, shortLink: string): string {
  return `${OFFLINE_MOVE_STORAGE_PREFIX}${userId}:${shortLink}`;
}

function offlineDescriptionStorageKey(userId: string, shortLink: string): string {
  return `${OFFLINE_DESCRIPTION_STORAGE_PREFIX}${userId}:${shortLink}`;
}

function offlineBoardStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function removeCorruptOfflineBoardSnapshot(storage: Storage, key: string): void {
  try {
    storage.removeItem(key);
  } catch {
    /* Ignore cleanup failures; refusing the corrupt value is the contract. */
  }
}

function dispatchOfflineDescriptionDraftsChanged(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(OFFLINE_DESCRIPTION_DRAFTS_CHANGED_EVENT));
}
